/**
 * Copyright (C) Siemens AG 1996-2025 ALL RIGHTS RESERVED.
 *
 *
 * AI Query Service
 *
 * OVERVIEW:
 * This service manages communication with the AI Query Service backend.
 * It handles thread creation and message sending for AI-powered chat interactions.
 *
 * KEY FEATURES:
 * - Create new AI conversation threads
 * - Send messages and receive AI-generated responses
 * - Handle API communication with proper error handling
 *
 * RELATED MODULES:
 * - AI Chatbot Component
 *
 * IMPLEMENTATION NOTES:
 * - Uses the AI Query Service API (port 3010 in local development)
 * - Thread IDs must be created before sending messages
 * - Responses include timestamps, text content, and optional references
 *
 * EXAMPLE USAGE:
 * ```typescript
 * const aiQueryService = new AiQueryService(httpClient);
 * const thread = await aiQueryService.createThread();
 * const response = await aiQueryService.sendMessage(thread.threadId, 'Hello AI');
 * console.log(response.text);
 * ```
 */
import {Injectable, inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../environments/environment';
import {ThreadResponse, MessageResponse} from '../models/AiQuery';

@Injectable({
  providedIn: 'root',
})
export class AiQueryService {
  private readonly http = inject(HttpClient);

  private readonly baseUrl = `${environment.backend.ai_query_host}`;

  /**
   * Creates a new AI conversation thread.
   *
   * IMPLEMENTATION DETAILS:
   * - Calls POST /threads endpoint
   * - Returns a unique thread ID for future message exchanges
   *
   * @returns {Observable<ThreadResponse>} Observable containing the thread ID
   * @throws Error when thread creation fails
   */
  createThread(): Observable<ThreadResponse> {
    return this.http.post<ThreadResponse>(`${this.baseUrl}/threads`, {});
  }

  /**
   * Sends a message to an existing thread and receives an AI-generated response.
   *
   * IMPLEMENTATION DETAILS:
   * - Calls POST /threads/{threadId}/messages endpoint
   * - Waits for AI processing and returns the complete response
   * - Response includes timestamp, text content, and optional file references
   *
   * @param {string} threadId - The unique identifier of the thread to send the message to
   * @param {string} text - The message text to send to the AI
   * @returns {Observable<MessageResponse>} Observable containing the AI response with text and references
   * @throws Error when message sending fails or thread ID is invalid
   */
  sendMessage(threadId: string, text: string): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(
      `${this.baseUrl}/threads/${threadId}/messages`,
      {text},
    );
  }
}
